import React from 'react';

// Final results regrouped by bidder: one block per bidder listing the bands it won, with clock
// and exit-bid quantities and payments, the bidder's total, and a total across all bidders.
// Built purely from the GET /auctions/{id}/results payload both screens already load; the
// by-band tables below it show the same rows grouped the other way. Nothing is recomputed:
// every figure is a sum of the per-band, per-bidder values the server allocated, so the grand
// total equals the payload's totalRevenue.

type Row = {
  categoryId: string;
  categoryName: string;
  clockQuantity: number;
  extensionQuantity: number;
  totalQuantity: number;
  clockPayment: number;
  extensionRevenue: number;
  totalPayment: number;
};

// Zero shows as a dash, so a bidder's exit-bid columns read as "none" rather than as a figure.
const fmt = (v: number) => (v ? v.toLocaleString() : '–');

export default function ResultsByBidder({ results }: { results: any }) {
  const byBidder: Record<string, { name: string; rows: Row[] }> = {};
  for (const cat of results.categories) {
    for (const a of cat.allocations) {
      if (!byBidder[a.bidderId]) byBidder[a.bidderId] = { name: a.bidderName, rows: [] };
      if (a.totalQuantity > 0) {
        byBidder[a.bidderId].rows.push({
          categoryId: cat.categoryId,
          categoryName: cat.categoryName,
          clockQuantity: a.clockQuantity,
          extensionQuantity: a.extensionQuantity,
          totalQuantity: a.totalQuantity,
          clockPayment: a.clockPayment,
          extensionRevenue: a.extensionRevenue,
          totalPayment: a.totalPayment,
        });
      }
    }
  }
  const bidders = Object.keys(byBidder)
    .map(id => byBidder[id])
    .sort((x, y) => x.name.localeCompare(y.name, undefined, { numeric: true }));
  const sum = (rows: Row[], key: keyof Row) => rows.reduce((s, r) => s + (r[key] as number), 0);
  const grandTotal = bidders.reduce((s, b) => s + sum(b.rows, 'totalPayment'), 0);

  return (
    <div data-testid="results-by-bidder" style={{ marginBottom: '1.5rem' }}>
      <h5 style={styles.heading}>Results by Bidder</h5>
      {bidders.map(b => (
        <div key={b.name} style={{ marginBottom: '1rem' }}>
          <div style={styles.bidderLine}>
            <strong>{b.name}</strong>
            <span>Total payment: <strong>{sum(b.rows, 'totalPayment').toLocaleString()}</strong></span>
          </div>
          {b.rows.length === 0 ? (
            <p style={styles.none}>No lots won.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Band</th>
                    <th style={styles.thNum}>Clock Qty</th>
                    <th style={styles.thNum}>Ext Qty</th>
                    <th style={styles.thNum}>Total Qty</th>
                    <th style={styles.thNum}>Clock Payment</th>
                    <th style={styles.thNum}>Ext Payment</th>
                    <th style={styles.thNum}>Total Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map(r => (
                    <tr key={r.categoryId}>
                      <td style={styles.td}>{r.categoryName}</td>
                      <td style={styles.tdNum}>{fmt(r.clockQuantity)}</td>
                      <td style={styles.tdNum}>{fmt(r.extensionQuantity)}</td>
                      <td style={styles.tdNum}>{fmt(r.totalQuantity)}</td>
                      <td style={styles.tdNum}>{fmt(r.clockPayment)}</td>
                      <td style={styles.tdNum}>{fmt(r.extensionRevenue)}</td>
                      <td style={styles.tdNum}>{fmt(r.totalPayment)}</td>
                    </tr>
                  ))}
                  <tr style={styles.totalRow}>
                    <td style={styles.td}><strong>Total</strong></td>
                    <td style={styles.tdNum}><strong>{fmt(sum(b.rows, 'clockQuantity'))}</strong></td>
                    <td style={styles.tdNum}><strong>{fmt(sum(b.rows, 'extensionQuantity'))}</strong></td>
                    <td style={styles.tdNum}><strong>{fmt(sum(b.rows, 'totalQuantity'))}</strong></td>
                    <td style={styles.tdNum}><strong>{fmt(sum(b.rows, 'clockPayment'))}</strong></td>
                    <td style={styles.tdNum}><strong>{fmt(sum(b.rows, 'extensionRevenue'))}</strong></td>
                    <td style={styles.tdNum}><strong>{fmt(sum(b.rows, 'totalPayment'))}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}
      <div style={{ ...styles.bidderLine, borderTop: '2px solid #ccc', paddingTop: '0.4rem' }}>
        <strong>All bidders</strong>
        <span>Total revenue: <strong data-testid="results-by-bidder-total">{grandTotal.toLocaleString()}</strong></span>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  heading: { margin: '0 0 0.6rem', fontWeight: 600, color: '#1e7e34', fontSize: '1rem' },
  bidderLine: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '0 0 0.3rem', fontSize: '0.95rem' },
  none: { margin: '0 0 0.5rem', color: '#777', fontSize: '0.875rem' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', marginBottom: '0.5rem' },
  th: { padding: '0.4rem 0.6rem', borderBottom: '2px solid #ddd', textAlign: 'left', fontWeight: 600, color: '#555' },
  thNum: { padding: '0.4rem 0.6rem', borderBottom: '2px solid #ddd', textAlign: 'right', fontWeight: 600, color: '#555' },
  td: { padding: '0.35rem 0.6rem', borderBottom: '1px solid #f0f0f0' },
  tdNum: { padding: '0.35rem 0.6rem', borderBottom: '1px solid #f0f0f0', textAlign: 'right' },
  totalRow: { borderTop: '2px solid #ddd', background: '#fafafa' },
};
